/** Transient inline hint that floats over the canvas. Used to give
 *  the user a one-line explanation when a gesture is intentionally
 *  refused (mirror-of-offset bidirectional edit, etc.) so the
 *  early-return doesn't feel like the app froze.
 *
 *  Singleton DOM node (`.canvas-toast`) auto-created on first show
 *  and re-used across calls. Auto-dismisses after `durationMs`
 *  (default 2500). Calling `showCanvasToast` again before the timer
 *  fires resets the message and restarts the timer. Stays tiny and
 *  decoupled (no editor wiring) — same shape as
 *  `snapAngleIndicator` next door.
 *
 *  A toast can carry one action button (the stuck-load "Reload", #1230),
 *  and `durationMs: null` keeps it up until dismissed. `showCanvasToast`
 *  returns a dismiss function that clears only that toast, not a later one
 *  that replaced it. */

let toastEl: HTMLDivElement | null = null;
let dismissTimer: number | null = null;
/** Bumped on every show, so a stale dismiss leaves a newer toast alone. */
let shown = 0;

function ensureEl(): HTMLDivElement {
  // Re-create if the cached node was removed from the document
  // (e.g., a test reset the DOM, or some external code cleared it).
  // Without the `isConnected` check the stale handle would point at
  // a detached node and `document.querySelector(".canvas-toast")`
  // wouldn't find it.
  if (toastEl && toastEl.isConnected) return toastEl;
  const el = document.createElement("div");
  el.className = "canvas-toast";
  document.body.appendChild(el);
  toastEl = el;
  return el;
}

export function showCanvasToast(
  message: string,
  opts?: {
    /** How long it stays up; null keeps it until dismissed. */
    durationMs?: number | null;
    action?: { label: string; run: () => void };
  },
): () => void {
  const el = ensureEl();
  el.textContent = message;
  const action = opts?.action;
  if (action) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "canvas-toast-action";
    btn.textContent = action.label;
    btn.addEventListener("click", action.run);
    el.append(" ", btn);
  }
  el.classList.toggle("canvas-toast--action", !!action);
  el.classList.add("canvas-toast--visible");
  if (dismissTimer !== null) window.clearTimeout(dismissTimer);
  dismissTimer = null;
  const durationMs = opts?.durationMs === undefined ? 2500 : opts.durationMs;
  if (durationMs !== null) {
    dismissTimer = window.setTimeout(() => {
      if (toastEl) toastEl.classList.remove("canvas-toast--visible");
      dismissTimer = null;
    }, durationMs);
  }
  const id = ++shown;
  return () => {
    if (id === shown) clearCanvasToast();
  };
}

export function clearCanvasToast(): void {
  if (toastEl) toastEl.classList.remove("canvas-toast--visible");
  if (dismissTimer !== null) {
    window.clearTimeout(dismissTimer);
    dismissTimer = null;
  }
}
